import React, { useRef, useImperativeHandle } from 'react';
import _merge from 'lodash/merge';
import Tokens from '@nokia-csf-uxr/freeform-design-tokens';
import Elevation from 'components/Elevation';

import Menu, { MenuProps, MenuRef } from 'components/Menu';

const RANGE_STYLE = (style = {}) => ({
  display: 'flex',
  flexDirection: 'column' as const,
  minWidth: '40rem',
  borderRadius: Tokens.RADIUS.RADIUS_2,
  maxHeight: '100vh',
  maxWidth: '100vw',
  overflow: 'auto',
  borderBox: 'box-sizing',
  ...style
});

const ELEVATION_INDEX = 24;

const MENU_CONFIG = {
  usePortal: true,
  positioningOptions: {
    modifiers: [
      {
        name: 'preventOverflow',
        options: {
          altAxis: true,
        },
      },
      {
        name: 'flip',
        options: {
          fallbackPlacements: ['top', 'right', 'left'],
        },
      },
      {
        name: 'offset',
        options: {
          offset: [-3, 1]
        }
      }
    ]
  }
};

export interface RangeProps extends React.ComponentPropsWithoutRef<'div'> {
  /** To render the `Range` in a popup and configure its positioning. */
  popupProps?: Partial<MenuProps>;
  /** The content of the <Range />. Consider using <RangeSection/>. */
  children: React.ReactNode;
  /** To customize the wrapper style. */
  style?: React.CSSProperties;
  /** To hide the Range when popupProps.triggerEl is not available. */
  hideWithoutTriggerEl?: boolean;
}

export type RangeHandle = {
  positionPopup: () => void;
  wrapperRef: () => React.RefObject<HTMLElement>;
};

/**
 * <Range/> is the wrapper of Range elements, it extends <Elevation /> component,
 * and all props but `popupProps` will be provided to the <Elevation/>, read <Elevation/> documentation to learn more.
 * To get the ref of the outermost element use `ref` prop. [Read React useRef hook documentation](https://reactjs.org/docs/hooks-reference.
 * */
const Range = React.forwardRef<RangeHandle, RangeProps>((props: RangeProps, ref) => {
  const {
    style,
    children,
    popupProps,
    hideWithoutTriggerEl,
    ...otherProps
  } = props;

  const menuRef = useRef<MenuRef>(null);
  const elevationRef = useRef(null);

  const elevation = (
    <Elevation
      style={RANGE_STYLE(style)}
      elevationIndex={ELEVATION_INDEX}
      ref={elevationRef}
      {...otherProps}
    >
      {children}
    </Elevation>
  );

  const resolvedPopupProps = _merge(
    {},
    MENU_CONFIG,
    popupProps
  );

  useImperativeHandle(ref, () => ({
    positionPopup: () => { menuRef.current && menuRef.current.positionMenu(); },
    wrapperRef: elevationRef.current || menuRef.current
  }));

  if (hideWithoutTriggerEl) {
    return resolvedPopupProps.triggerEl && resolvedPopupProps.triggerEl.current ? (
      <Menu {...resolvedPopupProps} ref={menuRef}>
        {elevation}
      </Menu>
    ) : null;
  }
  return (
    <Menu {...resolvedPopupProps} ref={menuRef}>
      {elevation}
    </Menu>
  );
});

Range.displayName = 'Range';

Range.defaultProps = {
  style: {},
  popupProps: {
    triggerEl: undefined
  },
  hideWithoutTriggerEl: false
};

export default Range;
